// COLUNAS DOS ARQUIVOS DA ALECE — nomes, tipos e dominios.
//
// Fonte: dicionario de dados escrito pela propria ALECE ("LAYOUT ALECE-ATLAS",
// 02/09/2026) e a analise dos arquivos da competencia 202608. O documento tem
// por titulo interno "CONSIGFACIL": e' a interface que a ALECE JA OPERA com a
// averbadora atual, nao uma proposta — dai ela ser fixa do lado de la, e o
// tradutor viver do nosso.
//
// A LEITURA E' POR NOME DE COLUNA, NAO POR POSICAO. O export e' um relatorio e
// a ordem pode mudar entre versoes; casar por nome custa nada e nao quebra.
// O campo `coluna` existe para o documento tecnico e para `validarLayoutColunas`
// — ele descreve a ordem PADRAO, nao uma exigencia de leitura.
//
// DIVERGENCIA CONHECIDA (pedido 7 do relatorio tecnico): o dicionario documenta
// `TIPO` como `B` (obrigatorio) e `N` (normal); os arquivos reais trazem `C` e
// `N`, sem nenhum `B`. `C` e' o unico com MOTIVO e com `N CONTRATO(S)` em OBS,
// entao a leitura e' que `C` = consignacao gerida pela averbadora, subtipo de
// `N`. Os tres codigos sao aceitos ate a ALECE confirmar.
/** Sobe quando coluna entra/sai ou regra de traducao muda. */
export const ALECE_LAYOUT_VERSAO = "1.2";
/**
 * 1.2: nove colunas, sem PARCELA/PARCTOT.
 * 1.3 (23/09/2026): separador `;`, decimal em virgula, nove colunas FIXAS
 * (sairam as opcoes PAGOFP/MOTIVO e parcela no OBS) e FF vindo do retorno.
 * 1.4 (23/09/2026): DESCRUB nunca e' "Credor a identificar" — sem rubrica no
 * catalogo, o credor conhecido ou vazio, com `DESCRUB` no relatorio.
 * 1.5 (24/09/2026): FF e DESCRUB obrigatorios — vazios, a emissao recusa com a
 * lista do que corrigir; o ensaio continua gerando e acusando (#177).
 * 1.6 (24/09/2026): a forma do movimento de julho, que a ALECE importa — FF com
 * 2 digitos e MATR com 6, zeros a esquerda (`04;000004`), e o cabecalho
 * terminando em `;` (`...;VALOR;`). As linhas de dados seguem sem `;` final.
 */
export const ALECE_MOVIMENTO_LAYOUT_VERSAO = "1.6";
/** Largura com zeros a esquerda no movimento, como o arquivo de julho/2026. */
export const ALECE_MOVIMENTO_LARGURA_FF = 2;
export const ALECE_MOVIMENTO_LARGURA_MATR = 6;
/**
 * Separador DOS EXEMPLOS e das fixtures, nao do layout: o catalogo declara
 * `separador: "detectar"`, e a entrada descobre `;`, virgula ou tabulacao pelo
 * cabecalho de cada arquivo (setembro/2026 chegou com `;`; agosto tinha sido
 * convertido a mao de tabulacao para virgula antes da analise). Os testes da
 * API que montam pacotes a partir do exemplo separam por esta constante.
 */
export const ALECE_SEPARADOR = ";";
/**
 * Separador do MOVIMENTO, o arquivo que a Atlas ENVIA. Decidido com a ALECE em
 * 23/09/2026: `;`, com decimal em virgula (`120,00`). Os arquivos que ela
 * MANDA (SERVIDOR e FOLHA) têm o delimitador detectado por arquivo.
 */
export const ALECE_SEPARADOR_MOVIMENTO = ";";
export const ALECE_DECIMAL_MOVIMENTO = ",";
/** `C` consignacao · `N` desconto normal · `B` obrigatorio (documentado, nao visto). */
export const ALECE_TIPOS = ["C", "N", "B"];
/** Motivos vistos na competencia 202608. */
export const ALECE_MOTIVOS = ["PGTO OK", "MARGEM INSUFICIENTE", "FALECIDO", "DESLIGADO"];
/** Motivos que, com a matricula ausente da base, declaram desligamento. */
export const ALECE_MOTIVOS_DESLIGAMENTO = ["FALECIDO", "DESLIGADO"];
/** `N CONTRATO(S)` — a linha soma N contratos do mesmo credor. */
export const ALECE_PADRAO_CONTRATOS = /^\s*(\d+)\s+CONTRATO\(S\)\s*$/i;
// ============================================================ arquivo SERVIDOR
const alfa = (nome, coluna, descricao, obrigatorio = false) => ({
    nome, coluna, kind: "alfa", descricao, ...(obrigatorio ? { obrigatorio } : {}),
});
const num = (nome, coluna, descricao, obrigatorio = false) => ({
    nome, coluna, kind: "num", descricao, ...(obrigatorio ? { obrigatorio } : {}),
});
const dinheiro = (nome, coluna, descricao, obrigatorio = false) => ({
    nome, coluna, kind: "dinheiro", descricao, ...(obrigatorio ? { obrigatorio } : {}),
});
/** Colunas do `ALECE-SERVIDOR-AAMM.TXT`, na ordem do dicionario. */
export const COLUNAS_ALECE_SERVIDOR = [
    num("FF", 1, "Folha funcional. Agrupa o servidor por tipo de folha (ativo, inativo, pensionista…).", true),
    alfa("DESCFF", 2, "Descrição da folha funcional."),
    alfa("REGIME", 3, "Regime pertinente à folha funcional (APOSENTADO, QUADRO PERMANENTE, COMISSIONADO…)."),
    alfa("MATR", 4, "Matrícula do servidor. Identidade dentro da folha.", true),
    alfa("CPF", 5, "CPF. Vem como número no export atual, então perde zeros à esquerda.", true),
    alfa("NOME", 6, "Nome do servidor.", true),
    { nome: "ANOMES", coluna: 7, kind: "competencia", obrigatorio: true, descricao: "Ano e mês de referência da folha, AAAAMM." },
    num("CODFP", 8, "Código da folha de pagamento.", true),
    alfa("DESCFP", 9, "Descrição da folha de pagamento."),
    alfa("ESTAB", 10, "Estabilidade. `S`/`N` no dicionário; `ESTAVEL`/`NAO ESTAVEL` nos arquivos."),
    alfa("DTINIFP", 11, "Data de início do servidor na folha. Não é a admissão original."),
    alfa("CODLOTA", 12, "Código da lotação (setor de trabalho)."),
    alfa("DESCLOTA", 13, "Descrição da lotação."),
    alfa("CARFUN", 14, "Código do cargo ou função."),
    alfa("DESCARFUN", 15, "Descrição do cargo ou função."),
    alfa("ESTCIV", 16, "Estado civil."),
    alfa("CONJUGE", 17, "Nome do cônjuge. **Não importado** — sem uso na Atlas."),
    alfa("SEXO", 18, "`M` ou `F`."),
    alfa("PAI", 19, "Filiação. **Não importado**."),
    alfa("MAE", 20, "Filiação. **Não importado**."),
    alfa("DTNASC", 21, "Data de nascimento."),
    alfa("RG", 22, "Número do RG. **Não importado**."),
    alfa("RGEXP", 23, "Órgão expedidor. **Não importado**."),
    alfa("RGUF", 24, "UF do RG. **Não importado**."),
    alfa("RGDT", 25, "Data de expedição. **Não importado**."),
    alfa("ESCOLARIDADE", 26, "Grau de escolaridade."),
    alfa("EMAIL", 27, "E-mail."),
    alfa("CELULAR", 28, "Celular com DDD."),
    alfa("FONE", 29, "Telefone fixo."),
    alfa("ENDLOG", 30, "Tipo de logradouro (R, AV, TV…)."),
    alfa("ENDERECO", 31, "Logradouro."),
    alfa("ENDNUM", 32, "Número."),
    alfa("ENDCOMPL", 33, "Complemento."),
    alfa("ENDBAIRRO", 34, "Bairro."),
    alfa("ENDCEP", 35, "CEP. Vem como número, então perde zero à esquerda."),
    alfa("ENDCIDADE", 36, "Município."),
    alfa("ENDUF", 37, "UF."),
    alfa("BANCO", 38, "Código do banco de crédito do salário."),
    alfa("AGENCIA", 39, "Agência."),
    alfa("CONTA", 40, "Conta."),
    dinheiro("SALBASE", 41, "Salário base para cálculo da margem.", true),
    dinheiro("MARGBRUTA", 42, "Margem bruta: 40% ou 60% de (SALBASE − DESCOBRIG), conforme M60."),
    dinheiro("MARGEM40", 43, "Margem disponível no teto de 40%."),
    dinheiro("MARGEM60", 44, "Margem disponível no teto de 60%."),
    dinheiro("DESCOBRIG", 45, "Total dos descontos obrigatórios (tipo B).", true),
    dinheiro("OUTROSDESC", 46, "Total dos descontos normais (tipo N)."),
    dinheiro("DESCREAL", 47, "Total dos descontos normais reais, sem o corte."),
    alfa("CORTE", 48, "`S` quando houve corte em alguma consignação."),
    alfa("M60", 49, "`S` quando o servidor tem direito à margem de 60% (tem plano de saúde)."),
    alfa("BLOQ", 50, "`S` quando está bloqueado para pedir nova consignação."),
];
/** Colunas sem as quais o arquivo de servidor não pode ser traduzido. */
export const OBRIGATORIAS_ALECE_SERVIDOR = [
    "ANOMES", "CODFP", "MATR", "CPF", "NOME", "SALBASE", "DESCOBRIG",
];
/**
 * Ordem das colunas 13+ da carga — os campos proprios da unidade.
 *
 * A ORDEM E' PARTE DO CONTRATO. `admin_servidor_campos_configs` mapeia
 * "coluna 13 → chave"; se esta lista mudar de ordem sem a config mudar junto, a
 * lotacao vira margem em silencio. Ver `carga-campos-custom.ts`.
 */
export const ALECE_EXTRAS_ORDEM = [
    "FF", "DESCFF", "ESTAB", "CODLOTA", "DESCLOTA", "CARFUN", "SEXO", "ESTCIV", "ESCOLARIDADE", "FONE",
    "SALBASE", "MARGBRUTA", "MARGEM40", "MARGEM60", "DESCOBRIG", "OUTROSDESC", "DESCREAL",
    "CORTE", "M60", "BLOQ",
    "EMAIL", "CELULAR", "DTNASC",
    "ENDLOG", "ENDERECO", "ENDNUM", "ENDCOMPL", "ENDBAIRRO", "ENDCEP", "ENDCIDADE", "ENDUF",
];
/** Colunas que a Atlas nao importa, por desenho (minimizacao de dado pessoal). */
export const ALECE_NAO_IMPORTADAS = [
    "CONJUGE", "PAI", "MAE", "RG", "RGEXP", "RGUF", "RGDT", "DESCFP",
];
// ============================================================ arquivo FOLHA
/** Colunas do `ALECE-FOLHA-AAMM.TXT`. */
export const COLUNAS_ALECE_FOLHA = [
    num("CODFP", 1, "Código da folha de pagamento.", true),
    { nome: "ANOMES", coluna: 2, kind: "competencia", obrigatorio: true, descricao: "Ano e mês de referência, AAAAMM." },
    num("FF", 3, "Folha funcional."),
    alfa("MATR", 4, "Matrícula do servidor.", true),
    alfa("CPF", 5, "CPF do servidor."),
    { nome: "TIPO", coluna: 6, kind: "codigo", obrigatorio: true, codigos: ALECE_TIPOS, descricao: "`C` consignação · `N` desconto normal · `B` desconto obrigatório." },
    alfa("RUB", 7, "Código da rubrica de desconto na folha.", true),
    alfa("DESCRUB", 8, "Descrição da rubrica."),
    alfa("OBS", 9, "Referência ou observação sobre o desconto. É onde a ADF deve voltar."),
    dinheiro("VALOR", 10, "Valor da rubrica informado pela averbadora.", true),
    dinheiro("PAGOFP", 11, "Valor efetivamente descontado do servidor na folha.", true),
    alfa("MOTIVO", 12, "Preenchido quando VALOR difere de PAGOFP."),
];
export const OBRIGATORIAS_ALECE_FOLHA = [
    "ANOMES", "CODFP", "MATR", "TIPO", "RUB", "VALOR", "PAGOFP",
];
// ============================================================ movimento (saida)
/**
 * Colunas do `ALECE-MOVIMENTO-AAMM.TXT`.
 *
 * "A mesma estrutura do arquivo FOLHA, sem precisar enviar as rubricas tipo B,
 * e sem precisar informar o CODFP, e acrescentando mais 2 colunas: PARCELA e
 * PARCTOT" — dicionario da ALECE, literal.
 */
export const COLUNAS_ALECE_MOVIMENTO = [
    { nome: "ANOMES", coluna: 1, kind: "competencia", obrigatorio: true, descricao: "Competência do desconto, AAAAMM." },
    num("FF", 2, "Folha funcional do servidor (comissionado, pensionista, aposentado…). Vem do arquivo SERVIDOR do retorno, pela foto de margem ou pelo compromisso do contrato. Sem ela a ALECE não lança o desconto: a emissão recusa.", true),
    alfa("MATR", 3, "Matrícula do servidor.", true),
    alfa("CPF", 4, "CPF do servidor, 11 dígitos sem máscara."),
    { nome: "TIPO", coluna: 5, kind: "codigo", obrigatorio: true, codigos: ["C"], descricao: "Sempre `C`: o movimento instrui consignação." },
    alfa("RUB", 6, "Código da rubrica de desconto, como o convênio declarou.", true),
    alfa("DESCRUB", 7, "Descrição da rubrica, do catálogo da unidade. Sem ela, o nome do credor conhecido; nunca \"Credor a identificar\". Vazia, o ensaio acusa e a emissão recusa.", true),
    alfa("OBS", 8, "Quantidade de contratos: `N CONTRATO(S)`."),
    dinheiro("VALOR", 9, "Valor a descontar na competência.", true),
];
//# sourceMappingURL=alece-colunas.js.map